/**
 * What is in the world, as a list.
 *
 * The viewport only shows what is in front of the camera, which is no help when
 * looking for the one zone that is not filling or the guard somebody put down on
 * the far shore two days ago. Everything placed deliberately is listed here:
 * zones and characters, what each of them holds, and whether it is working.
 *
 * Choosing a row selects it and looks at it, because those two are the same
 * thing in practice: nobody picks something out of a list in order to leave it
 * where it is.
 */

import type { NpcDefinitionWire, SpawnWire, WorldProblem, ZoneWire } from '@inverse/protocol';
import { useState } from 'react';
import { Explain } from '../Explain';

type Section = 'zones' | 'characters' | 'scenery' | 'problems';

/**
 * How much of the world a list is about.
 *
 * A continent has thousands of things placed in it, and a list of all of them
 * is a list nobody scrolls to the end of. What somebody nearly always wants is
 * what is around them, so that is what it opens on; everywhere is still there
 * for the guard placed on the far shore two days ago.
 */
type Scope = 'near' | 'all';

/** How far near reaches, in tiles. About what the camera can see, twice over. */
const NEAR = 120;

export function EditorOutliner({
  zones,
  spawns,
  characters,
  problems,
  problemCounts,
  census,
  selectedZone,
  /** Where the camera is looking, which is what near is measured from. */
  at,
  onSelectZone,
  onSelectSpawn,
  onCheck,
  onTidy,
  onCount,
  onStepTo,
  onGo,
}: {
  zones: readonly ZoneWire[];
  spawns: readonly SpawnWire[];
  characters: readonly NpcDefinitionWire[];
  /** What the last check found, or nothing when it has not been asked yet. */
  problems: readonly WorldProblem[] | null;
  /** The same problems counted by what they say, for the ones there are hundreds of. */
  problemCounts: readonly { kind: string; what: string; many: number }[];
  /** What is standing in the world by kind, counted when asked. */
  census: readonly { id: number; name: string; count: number }[] | null;
  selectedZone: number | null;
  at: { tx: number; tz: number };
  onSelectZone: (zone: ZoneWire) => void;
  onSelectSpawn: (spawn: SpawnWire) => void;
  onCheck: () => void;
  onTidy: () => void;
  onCount: () => void;
  onStepTo: (objectId: number) => void;
  onGo: (tx: number, tz: number) => void;
}): JSX.Element {
  const [section, setSection] = useState<Section>('zones');
  const [scope, setScope] = useState<Scope>('near');

  const nameOf = (npcId: number): string =>
    characters.find((entry) => entry.id === npcId)?.name ?? `Character ${npcId}`;

  /** How far something is from where the camera is looking, in tiles. */
  const away = (tx: number, tz: number): number =>
    Math.max(Math.abs(tx - at.tx), Math.abs(tz - at.tz));

  const zoneAway = (zone: ZoneWire): number => {
    let nearest = Infinity;
    for (const tile of zone.tiles) nearest = Math.min(nearest, away(tile.tx, tile.tz));
    return nearest;
  };

  // Nearest first, and only what is near unless somebody asks for everywhere.
  const zonesShown = [...zones]
    .map((zone) => ({ zone, off: zoneAway(zone) }))
    .filter((one) => scope === 'all' || one.off <= NEAR)
    .sort((one, other) => one.off - other.off);

  const spawnsShown = [...spawns]
    .map((spawn) => ({ spawn, off: away(spawn.tx, spawn.tz) }))
    .filter((one) => scope === 'all' || one.off <= NEAR)
    .sort((one, other) => one.off - other.off);

  const showing = section === 'zones' ? zonesShown.length : spawnsShown.length;
  const ofAll = section === 'zones' ? zones.length : spawns.length;

  return (
    <>
      <div className="editor-tabs">
        <button
          type="button"
          className={section === 'zones' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => setSection('zones')}
        >
          Zones
        </button>
        <button
          type="button"
          className={section === 'characters' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => setSection('characters')}
        >
          Characters
        </button>
        <button
          type="button"
          className={section === 'scenery' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => {
            setSection('scenery');
            onCount();
          }}
        >
          Scenery
        </button>
        <button
          type="button"
          className={section === 'problems' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => {
            setSection('problems');
            onCheck();
          }}
        >
          Problems
          {problems && problems.length > 0 ? (
            <span className="editor-count-quiet"> {problems.length}</span>
          ) : null}
        </button>
      </div>

      {section === 'zones' || section === 'characters' ? (
        <div className="editor-brushes outliner-scope">
          <button
            type="button"
            className={scope === 'near' ? 'editor-tab on' : 'editor-tab'}
            title="Only what is around where the camera is looking"
            onClick={() => setScope('near')}
          >
            In View
          </button>
          <button
            type="button"
            className={scope === 'all' ? 'editor-tab on' : 'editor-tab'}
            title="Everything in the world, however far away"
            onClick={() => setScope('all')}
          >
            Everywhere
          </button>
          <span className="editor-count-quiet">
            {showing === ofAll ? `${ofAll}` : `${showing} of ${ofAll}`}
          </span>
        </div>
      ) : null}

      <div className="editor-body">
        {section === 'zones' ? (
          <>
            {zonesShown.map(({ zone, off }) => {
              // A zone that never fills is nearly always one drawn over ground
              // nothing can stand on, and it is worth saying so here rather than
              // leaving somebody to wonder why the wood is empty.
              const full = zone.alive >= zone.max;
              const empty = zone.alive === 0;

              return (
                <button
                  key={zone.id}
                  type="button"
                  className={selectedZone === zone.id ? 'editor-row on' : 'editor-row'}
                  onClick={() => onSelectZone(zone)}
                >
                  <span className="editor-row-name">{zone.name || nameOf(zone.npcId)}</span>
                  <span
                    className={empty && !zone.paused ? 'editor-row-note quiet' : 'editor-row-note'}
                  >
                    {zone.alive} of {zone.max}
                    {zone.paused ? ' paused' : full ? '' : empty ? ' waiting' : ''}
                  </span>
                  <small>
                    {zone.tiles.length} tiles &middot; back every {(zone.minTicks * 0.6).toFixed(0)}{' '}
                    to {(zone.maxTicks * 0.6).toFixed(0)}s
                    {off > 40 ? ` \u00b7 ${off} tiles away` : ''}
                  </small>
                </button>
              );
            })}

            {zonesShown.length === 0 ? (
              <p className="hint">
                {zones.length === 0
                  ? 'No zones yet. Place something that fights in Spawns and one is drawn around it.'
                  : 'None near here. Everywhere shows the rest.'}
              </p>
            ) : null}
          </>
        ) : section === 'characters' ? (
          <>
            {spawnsShown.map(({ spawn, off }) => (
              <button
                key={`${spawn.tx},${spawn.tz}`}
                type="button"
                className="editor-row"
                onClick={() => onSelectSpawn(spawn)}
              >
                <span className="editor-row-name">{nameOf(spawn.npcId)}</span>
                <span className={spawn.alive ? 'editor-row-note' : 'editor-row-note quiet'}>
                  {spawn.alive ? 'there' : 'away'}
                </span>
                <small>
                  {spawn.tx}, {spawn.tz}
                  {off > 40 ? ` \u00b7 ${off} tiles away` : ''}
                </small>
              </button>
            ))}

            {spawnsShown.length === 0 ? (
              <p className="hint">
                {spawns.length === 0
                  ? 'Nobody is stationed anywhere.'
                  : 'Nobody near here. Everywhere shows the rest.'}
              </p>
            ) : null}
          </>
        ) : section === 'scenery' ? (
          <>
            <button type="button" className="wide" onClick={onCount}>
              Count what is out there
            </button>

            {census?.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="editor-row"
                onClick={() => onStepTo(entry.id)}
              >
                <span className="editor-row-name">{entry.name}</span>
                <span className="editor-row-note">{entry.count}</span>
                <small>click to walk the view through them</small>
              </button>
            ))}

            {census && census.length === 0 ? (
              <p className="hint">Nothing is standing anywhere in the world.</p>
            ) : null}
          </>
        ) : (
          <>
            <button type="button" className="wide" onClick={onCheck}>
              Look the world over
            </button>

            {problems?.some((problem) => /standing in the same place/.test(problem.what)) ? (
              <button
                type="button"
                className="wide"
                onClick={() => {
                  onTidy();
                  onCheck();
                }}
              >
                Take away what is standing on top of itself
              </button>
            ) : null}

            {problemCounts.length > 0 ? (
              <div className="editor-section">
                <h4>How many of each</h4>
                {problemCounts.map((one) => (
                  <div className="editor-fact" key={`${one.kind}|${one.what}`}>
                    <span>{one.what}</span>
                    <span>{one.many.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : null}

            {problems?.map((problem, index) => (
              <button
                key={`${problem.kind}-${index}`}
                type="button"
                className="editor-row"
                onClick={() => onGo(problem.tx, problem.tz)}
              >
                <span className="editor-row-name">{problem.what}</span>
                <span className="editor-row-note quiet">{problem.kind}</span>
                <small>
                  {problem.tx}, {problem.tz}
                </small>
              </button>
            ))}

            {problems && problems.length === 0 ? (
              <p className="hint">
                Nothing wrong that the tools can see.
                <Explain>
                  Every zone has room, everything stationed has somewhere to stand, and nothing
                  points at anything that is gone.
                </Explain>
              </p>
            ) : null}

            {problems === null ? (
              <p className="hint">
                Not looked yet.
                <Explain>
                  Zones drawn over water, characters stationed inside scenery, shops selling things
                  that no longer exist. None of it stops the world, which is why it goes unnoticed.
                </Explain>
              </p>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
